import { DataItem, GraviButton, Horizontal, Texto, Vertical, BBDTag } from '@gravitate-js/excalibrr'
import { formatDateStringToReadable, formatDateStringToTime } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Popover, Row, theme, Tooltip } from 'antd'
import classNames from 'classnames'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import CardError from '@assets/CustomIcons/credit-card-off.png'
import { Link } from 'react-router-dom'
import { CheckCircleFilled, DownOutlined, RightOutlined } from '@ant-design/icons'
import api from '@utils/api'
import { useScheduleContext } from '@components/OrderDispatch/Contexts/ScheduleContext'
import { FlagIconSwitch2 } from '@components/_SiteWide/Flags/FlagIconSwitch'

export function DispatchWindowPopover({ order, cardErrors, hideClass, isBackhaul, hoverTriggerItem, isExplicitView }) {
  const [showInstructions, setShowInstructions] = useState([])
  const isComplete = order.state === 'complete'
  const hasRunout = !isBackhaul && order.dispatch_window_end < order.drops[0].eta
  const noFit = !isBackhaul && order.dispatch_window_start > order.drops[0].eta

  const displayClass = useMemo(() => {
    if (!isBackhaul) {
      return cardErrors[0].status
    }
    if (cardErrors?.[0]?.type === 'card_alert' || cardErrors?.[0]?.type === 'unavailable') {
      return 'error'
    }
    return ''
  }, [isBackhaul, cardErrors])

  const content = (
    <DispatchPopoverContent
      order={order}
      cardErrors={cardErrors}
      hasRunout={hasRunout}
      noFit={noFit}
      hideClass={hideClass}
      isBackhaul={isBackhaul}
      showInstructions={showInstructions}
      setShowInstructions={setShowInstructions}
    />
  )
  const token = theme.useToken()

  return (
    <div>
      {!isComplete && (
        <Popover
          content={content}
          destroyTooltipOnHide={{ keepParent: false }}
          title={<DispatchPopOverTitle order={order} />}
          trigger='hover'
          onOpenChange={(visible) => setShowInstructions([])}
        >
          {displayClass === 'warning' ? (
            <GraviButton
              size='small'
              style={{
                marginRight: 0,
                width: isExplicitView ? 'min-content' : 30,
                fontSize: isExplicitView ? '1em' : '1.2em',
                backgroundColor: 'var(--theme-warning)',
              }}
              color='warning'
            >
              {hoverTriggerItem ? hoverTriggerItem : <FlagIconSwitch2 flag={cardErrors[0]} />}
            </GraviButton>
          ) : (
            <GraviButton
              size='small'
              error={displayClass === 'error'}
              className={classNames('window-status-icon', displayClass)}
              style={{ fontSize: isExplicitView ? '1em' : '1.2em', width: isExplicitView ? 'min-content' : 30 }}
            >
              {hoverTriggerItem ? hoverTriggerItem : <FlagIconSwitch2 flag={cardErrors[0]} />}
            </GraviButton>
          )}
        </Popover>
      )}
    </div>
  )
}
function DispatchPopoverContent({ order, cardErrors, isBackhaul, showInstructions, setShowInstructions }) {
  const hasCallIn = useMemo(() => order?.has_call_in_sites, [order])
  const retainLabel = hasCallIn ? 'Delivery Window Start' : 'Retain Time'
  const runoutLabel = hasCallIn ? 'Delivery Window End' : 'Estimated Runout'
  const retainStatus = useMemo(() => cardErrors.find((error) => error.type === 'retain')?.status, [cardErrors])
  const runoutStatus = useMemo(() => cardErrors.find((error) => error.type === 'runout')?.status, [cardErrors])

  // This seems like wonky code for displaying certain errors first but I don't want to break this so i'm sorry
  const driverScheduleIssues = useMemo(() => {
    return cardErrors.filter(
      (error) =>
        error.type === 'compartment product incompatibility' ||
        error.type === 'product incompatibility' ||
        error.type === 'volume discrepancy' ||
        error.type === 'product mismatch' ||
        error.type === 'supplier mismatch'
    )
  }, [cardErrors])

  const firstTwoErrors = useMemo(() => {
    return cardErrors.filter(
      (error) =>
        error.type !== 'compartment product incompatibility' &&
        error.type !== 'product incompatibility' &&
        error.type !== 'volume discrepancy' &&
        error.type !== 'product mismatch' &&
        error.type !== 'supplier mismatch'
    )
  }, [cardErrors, order?.has_call_in_sites])

  const cardAlertErrors = cardErrors.filter((error) => error.type === 'card_alert')

  return (
    <div>
      <Horizontal className='px-2'>
        <Col>
          <DataItem label='Estimated Terminal Arrival'>
            <b className='order-destination-location'>
              {formatDateStringToTime(order.loads[0].eta, order.loads[0].eta_local)}
            </b>
          </DataItem>
        </Col>
        <Horizontal verticalCenter justifyContent='flex-end' flex={1}>
          <DataItem
            valueStyle={{ textAlign: 'right' }}
            label={
              <Texto align='right' category='label'>
                Estimated Drop Time
              </Texto>
            }
          >
            <b style={{ textAlign: 'right' }} className='order-destination-location'>
              {formatDateStringToTime(order.drops[0].eta, order.drops[0].eta_local)}
            </b>
          </DataItem>
        </Horizontal>
      </Horizontal>

      <Horizontal className='px-2'>
        <Col>
          <DataItem label={retainLabel}>
            <b className={`popover-text-${retainStatus}`}>
              {formatDateStringToReadable(order.dispatch_window_start, order.dispatch_window_start_local)}
            </b>
          </DataItem>
        </Col>
        <Horizontal verticalCenter justifyContent='flex-end' flex={1}>
          <DataItem
            valueStyle={{ textAlign: 'right' }}
            label={
              <Texto align='right' category='label'>
                {runoutLabel}
              </Texto>
            }
          >
            <b style={{ textAlign: 'right' }} className={` popover-text-${runoutStatus}`}>
              {formatDateStringToReadable(order.dispatch_window_end, order.dispatch_window_end_local)}
            </b>
          </DataItem>
        </Horizontal>
      </Horizontal>
      {isBackhaul ? (
        <Vertical>
          {cardAlertErrors.map((e, i) => (
            <WindowStatusMessage
              key={i}
              cardStatus={e}
              showInstructions={showInstructions}
              setShowInstructions={setShowInstructions}
              index={i}
            />
          ))}
        </Vertical>
      ) : (
        <Vertical>
          {firstTwoErrors.map((e, i) => (
            <WindowStatusMessage
              key={i}
              cardStatus={e}
              showInstructions={showInstructions}
              setShowInstructions={setShowInstructions}
              index={i}
            />
          ))}
        </Vertical>
      )}
      {driverScheduleIssues.map((e, i) => (
        <WindowStatusMessage
          key={i}
          cardStatus={e}
          showInstructions={showInstructions}
          setShowInstructions={setShowInstructions}
          index={i}
        />
      ))}
    </div>
  )
}

function DispatchPopOverTitle({ order }) {
  return (
    <Row flex={1} justify='space-between' align='bottom'>
      <Col>
        <Texto category='h6'>Dispatch Details</Texto>
      </Col>
      <Col align='bottom'>
        <Texto category='h6'>{formatters.DATE(moment(order.loads[0].eta))}</Texto>
      </Col>
    </Row>
  )
}

function WindowStatusMessage({ cardStatus, showInstructions, setShowInstructions, index }) {
  const { refetchDriverSchedule } = useScheduleContext()
  if (cardStatus.type === 'card_alert') {
    const activityIssues =
      !cardStatus?.item?.activity_threshold_met && cardStatus?.item?.activity_count
        ? `(${cardStatus.item.activity_count}/${cardStatus.item.activity_threshold} ${cardStatus.item.activity}) `
        : ' '
    const key = `${cardStatus.item.driver_id}-${cardStatus.item.card_id}-${cardStatus.item.terminal_id}`
    return (
      <Vertical className={'mb-1 mt-1 full-height-width'}>
        <Horizontal
          style={{ backgroundColor: 'var(--theme-error-dim)' }}
          className={'p-1 pl-2 full-height-width'}
          verticalCenter
        >
          <img src={CardError} alt={'card error image'} style={{ height: '13px' }} />
          <Tooltip title={cardStatus.item.card_name}>
            <Link
              to={`/driver/carding?${new URLSearchParams({
                key,
              })}`}
              className={'ml-2'}
              target={'_blank'}
            >
              <Horizontal verticalCenter className={'p-1'}>
                <Texto>
                  <u> {cardStatus.item.status === 'Expired' ? 'Driver Card Expired' : 'Driver Not Carded'}</u>
                  {activityIssues} at {cardStatus.item.terminal_name}
                </Texto>
              </Horizontal>
            </Link>
          </Tooltip>

          {cardStatus.item.carding_instructions && (
            <Horizontal justifyContent={'end'} verticalCenter width={175}>
              <Texto>Carding Instructions</Texto>
              <GraviButton
                icon={showInstructions?.includes(index) ? <DownOutlined /> : <RightOutlined />}
                onClick={() =>
                  setShowInstructions((prev) =>
                    prev && prev?.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
                  )
                }
                className={`p-0`}
                style={{ backgroundColor: 'var(--theme-error-dim)' }}
              />
            </Horizontal>
          )}
        </Horizontal>
        {showInstructions?.includes(index) && (
          <Horizontal className={'px-2 pb-2'} style={{ backgroundColor: 'var(--theme-error-dim)' }} verticalCenter>
            <Texto className={'ml-4 mr-2'}> {cardStatus.item.carding_instructions}</Texto>
          </Horizontal>
        )}
      </Vertical>
    )
  } else if (
    cardStatus.type === 'compartment product incompatibility' ||
    cardStatus.type === 'product incompatibility'
  ) {
    return (
      <div
        className={classNames(
          'window-status-message mt-1 round-border',
          { 'bg-error': cardStatus.status === 'error' },
          { 'bg-warning': cardStatus.status === 'warning' }
        )}
      >
        <Row className='py-3 pr-2'>
          <Col span={4} className='vertical-flex-center items-center'>
            <Texto appearance='white' category='h4'>
              {cardStatus.icon}
            </Texto>
          </Col>
          <Col span={20}>
            <Texto weight='bold' appearance='white'>
              {cardStatus.message}
            </Texto>
            <Texto category='label' appearance='white'>
              {cardStatus.description.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </Texto>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'window-status-message mt-1 round-border',
        { 'bg-error': cardStatus.status === 'error' },
        { 'bg-warning': cardStatus.status === 'warning' },
        { 'bg-success': cardStatus.status === 'success' }
      )}
    >
      <Row className='py-3 pr-2'>
        <Col span={4} className='vertical-flex-center items-center'>
          <Texto appearance='white' category='h4'>
            {cardStatus.icon}
          </Texto>
        </Col>
        <Col span={20}>
          <Texto weight='bold' appearance='white'>
            {cardStatus.message}
          </Texto>
          <Texto category='label' appearance='white'>
            {cardStatus.description}
          </Texto>
          {cardStatus.resolvable && (
            <GraviButton
              onClick={async () => {
                await api.resolveDriverAlert(cardStatus.id, cardStatus.driverScheduleId)
                await refetchDriverSchedule()
              }}
              icon={<CheckCircleFilled />}
              className='flex-1 mt-2'
              buttonText='Mark As Resolved'
            />
          )}
        </Col>
      </Row>
    </div>
  )
}
